import React, { useEffect, useState } from 'react';
import { Modal } from 'react-responsive-modal';
import { Box, Typography } from '@mui/material';
import { MasterMessage } from '../../../../masters/masterMessages';
import Grid from '@mui/material/Grid';
import ButtonGeneral from '../../../components/general/buttons/buttonGeneral';
import Save from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import TimePickerField from '../../../components/general/fields/TimePickerField';
import FieldText from '../../../components/general/fields/fieldText';
import {
    asyncPatchtFrimeTime,
    asyncPostConfigTimeFrame,
} from '../../../../services/weCallYouAdmin.service';
import { SwalCustomClass } from '../../../components/general/toastSwal/swalCustomClass';
import dayjs from 'dayjs';

type Props = {
    open: boolean;
    onClose: (value: boolean) => void;
    onSave: (value: boolean) => void;
    rowTimeFrame?: any;
};

const ModalTimeFrameConfig = ({
    open,
    onClose,
    onSave,
    rowTimeFrame,
}: Props) => {
    const startData = rowTimeFrame
        ? dayjs(`2023-09-13 ${rowTimeFrame?.start}`)
        : '';
    const endData = rowTimeFrame
        ? dayjs(`2023-09-13 ${rowTimeFrame?.end}`)
        : '';
    const [startTime, setStartTime] = useState<any>(null);
    const { modalTimeFrameConfig }: any = MasterMessage.weCallYouAdmin;

    useEffect(() => {
        if (startData) {
            setStartTime(startData);
        }
    }, []);

    const formik = useFormik({
        initialValues: {
            start: startData,
            end: endData,
            maxDating: rowTimeFrame?.maxDating || '',
        },
        validationSchema: Yup.object({
            start: Yup.string().required(
                'El inicio de la franja es obligatorio'
            ),
            end: Yup.string().required('El fin de la franja es obligatorio'),
            maxDating: Yup.number()
                .min(1, 'El número debe ser mayor a 0')
                .typeError('El número de días debe ser un número')
                .required('El número de llamadas es obligatorio'),
        }),
        onSubmit: async (formModalTimeFrameConfig) => {
            formModalTimeFrameConfig.start = dayjs(
                formModalTimeFrameConfig.start
            ).format('HH:mm');
            formModalTimeFrameConfig.end = dayjs(
                formModalTimeFrameConfig.end
            ).format('HH:mm');
            if (rowTimeFrame) {
                asyncPatchtFrimeTime(
                    rowTimeFrame.id,
                    formModalTimeFrameConfig
                ).then((body: any) => {
                    if (body.items !== undefined) {
                        onClose(false);
                        onSave(true);
                        formik.handleReset;
                        SwalCustomClass.fire({
                            text: modalTimeFrameConfig.messageEdit,
                            icon: 'success',
                            iconColor: '#1d1d1d',
                        });
                    } else if (body?.messages !== undefined) {
                        SwalCustomClass.fire({
                            text: modalTimeFrameConfig[body.message],
                            icon: 'success',
                            iconColor: '#1d1d1d',
                        });
                    }
                });
            } else {
                const body: any = await asyncPostConfigTimeFrame(
                    formModalTimeFrameConfig
                );
                if (body?.items !== undefined) {
                    onClose(false);
                    onSave(true);
                    formik.handleReset;
                    SwalCustomClass.fire({
                        text: modalTimeFrameConfig.messageNew,
                        icon: 'success',
                        iconColor: '#1d1d1d',
                    });
                } else if (body?.messages !== undefined) {
                    SwalCustomClass.fire({
                        text: modalTimeFrameConfig[body.message],
                        icon: 'success',
                        iconColor: '#1d1d1d',
                    });
                }
            }
        },
    });

    const validateStartTime = (value: any) => {
        setStartTime(value);
        formik.setFieldValue('start', value, true);
    };

    return (
        <Modal
            open={open}
            onClose={() => onClose(false)}
            closeOnOverlayClick={false}
            styles={{ modal: { maxWidth: '500px', minWidth: '40%' } }}
        >
            <Box>
                <Typography
                    color="primary"
                    component="h3"
                    sx={{
                        fontSize: { lg: 27, xs: 23 },
                        mt: { xs: 1.5, sm: 1.5, md: 1.5, lg: 0 },
                    }}
                >
                    {rowTimeFrame
                        ? modalTimeFrameConfig.titleEdit
                        : modalTimeFrameConfig.titleNew}
                </Typography>
                <hr />
                <Box mt={3} px={1}>
                    <Grid
                        container
                        spacing={{ xs: 2, md: 2 }}
                        columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                    >
                        <Grid item xs={12}>
                            <TimePickerField
                                label="Selecciona el inicio de la franja horaria"
                                name="start"
                                size="small"
                                required={true}
                                touched={formik.touched.start}
                                errors={formik.errors.start}
                                value={formik.values.start}
                                onChange={(value) => validateStartTime(value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TimePickerField
                                label="Selecciona el final de la franja horaria"
                                name="end"
                                size="small"
                                required={true}
                                disabled={!startTime}
                                minTime={startTime}
                                touched={formik.touched.end}
                                errors={formik.errors.end}
                                value={formik.values.end}
                                onChange={(value) =>
                                    formik.setFieldValue('end', value, true)
                                }
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FieldText
                                label="Numero de llamadas disponibles"
                                name="maxDating"
                                size="small"
                                required={true}
                                touched={formik.touched.maxDating}
                                errors={formik.errors.maxDating}
                                value={formik.values.maxDating}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ textAlign: 'right' }}>
                            <ButtonGeneral
                                onClick={formik.handleSubmit}
                                startIcon={<Save color="secondary" />}
                                title="Guardar"
                                sx={{ mr: 1 }}
                            />
                            <ButtonGeneral
                                onClick={() => onClose(false)}
                                startIcon={<CancelIcon color="secondary" />}
                                title="Cancelar"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Modal>
    );
};

export default ModalTimeFrameConfig;
